import moment from 'moment-timezone';
import { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import AvatarIcon from '../../../assets/svg/AvatarIcon';
import RefreshIcon from '../../../assets/svg/RefreshIcon';
import { DATE_TIME_FORMAT, STATUS_BG_COLOR_MAP } from '../../../constants';
import { applyServerTimezone } from '../../../helpers';
import { BasicModal, Button, Loader } from '../../Shared';

const ApptStatus = ({ recordFieldNewValue }) => {
  return (
    <>
      <span className="text-base-1000 text-[16px]/[24px]">
        Status changed to
      </span>
      <div
        className={twMerge(
          'text-base-1000 font-medium text-[16px]/[24px] py-0.5 px-1.5 rounded-lg',
          STATUS_BG_COLOR_MAP[recordFieldNewValue],
        )}
      >
        {recordFieldNewValue}
      </div>
    </>
  );
};

const RescheduleDateTime = ({ recordFieldOldValue, recordFieldNewValue }) => {
  return (
    <>
      <div className="text-base-1000 font-medium text-[16px]/[24px] py-0.5 px-1.5 rounded-lg bg-brand-300/20">
        Rescheduled
      </div>
      <span className="text-base-1000 text-[16px]/[24px]">
        {`from ${recordFieldOldValue} to ${recordFieldNewValue}`}
      </span>
    </>
  );
};

const getContentByRecordFieldName = historyLog =>
  ({
    ApptStatus: <ApptStatus {...historyLog} />,
    rescheduleDateTime: <RescheduleDateTime {...historyLog} />,
  })[historyLog.recordFieldName];

const STATE = {
  HAS_DATA: 'hasData',
  HAS_NO_DATA: 'hasNoData',
  IS_DATA_LOADING: 'isDataLoading',
  IS_DATA_ERROR: 'isDataError',
};

const getState = (data, isDataLoading, isDataError) => {
  if (isDataLoading) return STATE.IS_DATA_LOADING;
  if (isDataError) return STATE.IS_DATA_ERROR;
  const hasNoData = data.length === 0;
  if (hasNoData) return STATE.HAS_NO_DATA;
  return STATE.HAS_DATA;
};

export const ActivityLogModal = ({ isAdmin, open, apptID, onClose }) => {
  const [bookingHistory, setBookingHistory] = useState([]);
  const [isBookingHistoryLoading, setIsBookingHistoryLoading] = useState(true);
  const [isBookingHistoryError, setIsBookingHistoryError] = useState(false);

  const fetchReports = useCallback(async () => {
    setIsBookingHistoryLoading(true);
    setIsBookingHistoryError(false);

    try {
      const response = await fetch(
        isAdmin
          ? `/admin/booking-history?ApptID=${apptID}`
          : `/booking-history?ApptID=${apptID}`,
      );
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      setIsBookingHistoryLoading(false);
      setBookingHistory(data.data);
    } catch {
      setIsBookingHistoryLoading(false);
      setIsBookingHistoryError(true);
    }
  }, [apptID, isAdmin]);

  useEffect(() => {
    if (!apptID) return;
    fetchReports();
  }, [apptID, fetchReports]);

  const state = getState(
    bookingHistory,
    isBookingHistoryLoading,
    isBookingHistoryError,
  );

  return (
    <BasicModal open={open} onClose={onClose}>
      <div className="bg-white w-[560px] rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
        <div className="flex items-center justify-between py-4 pl-5 pr-4 border-b border-b-base-200">
          <span className="text-base-1000 font-semibold text-[22px]/[28px]">
            Activity Log
          </span>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-transparent text-sm font-semibold text-gray-800 transition hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-gray-700 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
            data-hs-overlay="#edit-contact-modal"
            onClick={onClose}
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-4 w-4 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <div className="max-h-[380px] overflow-auto">
          {state === STATE.IS_DATA_LOADING && (
            <div className="h-[228px] flex justify-center items-center p-6">
              <Loader />
            </div>
          )}
          {state === STATE.IS_DATA_ERROR && (
            <div className="h-[228px] flex justify-center items-center flex-col p-6 gap-3">
              <div className="flex justify-center items-center flex-col gap-1.5">
                <span className="text-base-1000 text-[22px]/[28px] font-semibold">
                  Failed to load reports
                </span>
                <span className="text-base-800 text-[16px]/[24px]">
                  Error occurred while loading. Try again.
                </span>
              </div>
              <Button
                title="Reload"
                variant="text"
                className="text-destructive-500"
                iconStart={
                  <RefreshIcon className="w-5 h-5 text-destructive-500" />
                }
                onClick={fetchReports}
              />
            </div>
          )}
          {state === STATE.HAS_NO_DATA && (
            <div className="h-[228px] flex justify-center items-center p-6">
              <span className="text-base-800 text-[16px]/[24px]">
                There are no records to display
              </span>
            </div>
          )}
          {state === STATE.HAS_DATA &&
            bookingHistory.map((v, idx) => (
              <div key={idx} className="flex flex-col gap-1 py-3 px-5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 truncate">
                    <AvatarIcon className="w-5 h-5" />
                    <span className="text-base-1000 font-medium text-[14px]/[20px] truncate">
                      {v.changedBy}
                    </span>
                  </div>
                  <span className="text-base-600 text-[14px]/[20px] text-nowrap">
                    {moment(applyServerTimezone(v.updatedAt)).format(
                      DATE_TIME_FORMAT.MM_DD_YYYY_hh_mm_A,
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-[5px]">
                  {getContentByRecordFieldName(v)}
                </div>
              </div>
            ))}
        </div>
      </div>
    </BasicModal>
  );
};
